import {
    ScoredLoan,
    LoanType,
    Type,
    LoanInterestRateType,
    AlternativeProductCategory,
    AlternativeProduct,
    CardsDownsellProduct,
    LoanProductCategory,
} from '@mediaingenuity/totallymoney.types'
import { z } from 'zod'

export interface LoanErrorMessage {
    fieldName: string
    message: string
}

export enum LoanName {
    CarFinanceBroker = 'Car Finance Broker',
    CarLoan = 'Car Loan',
    GuarantorLoan = 'Guarantor Loan',
    HirePurchase = 'Hire Purchase',
    PersonalLoan = 'Personal Loan',
    SecuredLoan = 'Secured Loan',
}

export enum AlternativeProductsDetailsCategoryType {
    AlternativeProduct = 'AlternativeProduct',
    CreditCardsCrossSell = 'CreditCardsCrossSell',
    Drafty = 'Draft',
    LowerLoanAmount = 'LowerLoanAmount',
}

export enum ScoringStatus {
    ScoresExpired = 'ScoresExpired',
    ServerError = 'ServerError',
    Success = 'Success',
    UserNotFound = 'UserNotFound',
    ValidationError = 'ValidationError',
}

export const ScoredExperimentVariantKeysSchema = z.union([
    z.literal('loan_ranking_productrecommender'),
    z.literal('loan_ranking_hierarchical'),
    z.literal('control'),
])

type VariantKeys = z.infer<typeof ScoredExperimentVariantKeysSchema>

interface ScoredExperimentMatchFactorRankingLoans {
    name: 'experiment_useMatchFactorRankingLoans'
    variant: VariantKeys
}

export type ScoredExperimentLoans = ScoredExperimentMatchFactorRankingLoans
export type ScoredExperimentLoansArray =
    | ScoredExperimentLoans[]
    | []
    | undefined

export interface GetScoredLoansResponse {
    errorMessages?: LoanErrorMessage[]
    experiments: ScoredExperimentLoans[]
    leadId: string
    loanAmount: number
    loanTerm: number
    scores: ScoredLoan[]
    status: ScoringStatus
    thirdPartyReference: string
}

export interface PostScoredAlternativeProductsResponse {
    categories: AlternativeProductCategory[]
}

export interface PostCardsDownsellProductsResponse {
    categoryType: AlternativeProductsDetailsCategoryType
    cons?: string[]
    cta: string
    description?: string
    heading: string
    name: string
    productId: string
    productType: AlternativeProductsDetailsCategoryType
    products: CardsDownsellProduct[]
    pros?: string[]
    queryParams: { [key: string]: string }
}

export interface PostCarFinanceApplicationRequest {
    clickId: string
    customerId: string
    dailyId?: string
    leadId: string
    loanAmount?: number
    loanTerm?: number
    permanentId?: string
    productId?: string
    provider?: string
    providerId?: string
    quoteId?: string
    ref?: string
    visitGuid?: string
}

export type PostCarFinanceApplicationResponse = boolean

// Not from app
export interface SessionGuids {
    dailyId: string
    permanentId: string
    visitGuid: string
}

export type GetUnscoredLoansResponse = UnscoredLoanProduct[]

export interface UnscoredLoanProduct {
    acceptanceRate?: number
    activeCategories?: LoanProductCategory[]
    additionalOfferDescription: string
    additionalOfferTitle: string
    annualInterestRate?: number
    apr: number
    arrangementFee?: number
    arrangementFeePercentage?: number
    broker: string
    carModelType: string
    ccdAnnualInterestRate?: number
    ccdAprFixedVariable?: number
    ccdArrangementFee?: number
    ccdAssumedBorrowing?: number
    ccdLoanTerm?: number
    ccdMonthlyPayment?: number
    ccdTotalRepayable?: number
    enableSecureToken?: boolean
    excludeFromCarPurchase: boolean
    exclusiveLoan?: boolean
    hasAdditionalOffer: boolean
    hasOffer: boolean
    imageUrl: string
    interestRateType: LoanInterestRateType
    legalName: string
    lender: string
    loanId: string
    loanName: string
    loanType: LoanType
    maxAmount?: number
    maxTerm?: number
    minAmount?: number
    minTerm?: number
    monthlyPayment?: number
    offerDescription: string
    offerTitle: string
    propositionId: string
    propositionName: string
    relatedProductCodes: string

    representativeExample: string
    totalCharge?: number
    totalRepayable?: number

    type: Type
    typicalApr?: number
}

export type GetProductsResponse = UnscoredLoanProduct[]

export type GetAlternativeProductsResponse = AlternativeProduct[]

// Custom api

export interface UnscoredLoanProductsCategory {
    displayName: string
    id: LoanProductCategory
    loans: UnscoredLoanProduct[]
    templateId: LoanProductCategory
    totalLoans: number
}

export interface GetUnscoredCategoriesFilter {
    loanAmount: number
    loanTerm: number
}

export interface GetUnscoredCategoriesResponse {
    alternativeProducts: AlternativeProduct[]
    filter: GetUnscoredCategoriesFilter
    unscoredCategories: UnscoredLoanProductsCategory[]
}

export enum LoanPurpose {
    ConsolidateOtherDebts = 'Consolidate Other Debts',
    Holiday = 'Holiday',
    HomeImprovement = 'Home Improvement',
    Other = 'Other',
    VehiclePurchase = 'Vehicle Purchase',
    Wedding = 'Wedding',
}
